import React, { useEffect, useState } from "react";
import "./PolicyDrawer.css";
import { LinkRecord, PolicyProject } from "winslow-core";
import { PolicyStudioIconButton } from "./components/PolicyStudioIconButton";
import { handleCopyText } from "../uiHelpers";

import { MyTooltip } from "../common/MyTooltip";
import { LinkRecordActivity } from "../shares/LinkRecordActivity";
import PolicyStudioActionButton from "./components/PolicyStudioActionButton";
import { Spinner } from "../common/Spinner";
import { copyMarkdownToClipboard } from "../../hooks/usePolicyStudio";
import { Copy, Download } from "lucide-react";
import { showdownConverter } from "winslow-core";

interface DrawerPolicyConflictsProps {
  isOpen: boolean;
  onClose: () => void;
  linkRecords: LinkRecord[];
  handleCheckConflicts: (linkRecordIds: string[]) => void;
  conflictsCheckResults: PolicyProject["conflicts"] | undefined;
  isLoading: boolean;
  exportToPdf: (title: string, content: string) => void;
  isDirty: boolean;
  isPublic: boolean;
}

const MAX_TOKENS = 100000;

const DrawerPolicyConflicts: React.FC<DrawerPolicyConflictsProps> = ({
  isOpen,
  onClose,
  linkRecords,
  handleCheckConflicts,
  conflictsCheckResults,
  isLoading,
  exportToPdf,
  isDirty,
  isPublic,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedResources, setSelectedResources] = useState<string[]>([]);

  const [totalTokens, setTotalTokens] = useState<number>(0);
  const [isTokenAlertOpen, setIsTokenAlertOpen] = useState<boolean>(false);
  const [conflictsResults, setConflictsResults] = useState<
    PolicyProject["conflicts"] | undefined
  >(conflictsCheckResults);

  useEffect(() => {
    setConflictsResults(conflictsCheckResults);
  }, [conflictsCheckResults]);

  const filteredLinkRecords = linkRecords.filter((record) => {
    if (record.links?.[0]?.title === "(title unknown)") {
      return searchQuery.length === 0;
    }

    const searchTerm = searchQuery.toLowerCase();
    const title = record.links?.[0]?.title?.toLowerCase() || "";
    const url = record.links?.[0]?.url?.toLowerCase() || "";
    return title.includes(searchTerm) || url.includes(searchTerm);
  });

  const handleSelectAll = () => {
    setSelectedResources(
      selectedResources.length === linkRecords.length
        ? []
        : linkRecords.map((r) => r.id)
    );
  };

  const handleCopy = () => {
    if (conflictsResults?.conflictsContent) {
      copyMarkdownToClipboard(conflictsResults.conflictsContent);
    }
  };

  useEffect(() => {
    const totalTokens = selectedResources.reduce((acc, curr) => {
      const linkRecord = linkRecords.find((lr) => lr.id === curr);
      if (!linkRecord) return acc;
      return acc + linkRecord.tokenCount;
    }, 0);
    setTotalTokens(totalTokens);

    if (totalTokens > MAX_TOKENS) {
      setIsTokenAlertOpen(true);
    }
  }, [selectedResources]);

  return (
    <React.Fragment>
      <div
        className={`policy-drawer-overlay ${isOpen ? "open" : ""}`}
        onClick={() => {
          setConflictsResults(conflictsCheckResults);
          onClose();
        }}
      />
      <div className={`policy-drawer ${isOpen ? "open" : ""}`}>
        <div className="policy-drawer-header">
          <div className="policy-drawer-header-content">
            <div className="policy-drawer-title-container">
              <h2 className="policy-drawer-title">
                {isPublic
                  ? "Conflicts"
                  : conflictsResults
                    ? "Conflicts Report"
                    : "Find Conflicts with These Resources"}
              </h2>
              {!isPublic && !conflictsResults && (
                <div className="policy-drawer-subtitle">Resources</div>
              )}
            </div>
            <div className="policy-drawer-controls">
              <div className="policy-drawer-controls-row">
                <div
                  className="policy-drawer-close"
                  onClick={() => {
                    setConflictsResults(conflictsCheckResults);
                    onClose();
                  }}>
                  <img
                    src="/icons/close-icon.svg"
                    alt="Close"
                    className="policy-drawer-icon"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="drawer-spinner-container">
          {isLoading && <Spinner />}
        </div>
        {isPublic ? (
          <div className="policy-drawer-public-message">
            With the full version of the policy studio you can check your policy
            against other resources to find conflicts.
          </div>
        ) : (
          <div
            className={`policy-drawer-search ${isLoading ? "blur-content" : ""}`}>
            {!conflictsResults && (
              <>
                <div role="search" className="policy-drawer-search-input">
                  <input
                    type="text"
                    placeholder="Search conflicts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Search conflicts"
                    disabled={isLoading}
                  />

                  <div className="policy-drawer-check-conflicts">
                    <PolicyStudioActionButton
                      label="Check Conflicts"
                      onClick={() => handleCheckConflicts(selectedResources)}
                      disabled={
                        isLoading ||
                        selectedResources.length === 0 ||
                        totalTokens > MAX_TOKENS
                      }
                      variant="tertiary"
                    />
                  </div>
                </div>

                {!conflictsResults && (
                  <div className="policy-drawer-select-all">
                    <div className="row ">
                      <input
                        type="checkbox"
                        id="selectAll"
                        className="policy-drawer-checkbox"
                        checked={
                          selectedResources.length === linkRecords.length
                        }
                        onChange={handleSelectAll}
                        disabled={isLoading}
                      />
                      <label htmlFor="selectAll">Select all</label>
                    </div>
                    {totalTokens > MAX_TOKENS && (
                      <div className="policy-drawer-token-alert">
                        You have exceeded the maximum token count.
                      </div>
                    )}
                  </div>
                )}
                <div className="policy-drawer-resource-list">
                  <div className="policy-drawer-resource-container">
                    {filteredLinkRecords.map((linkRecord) => (
                      <LinkRecordActivity
                        key={linkRecord.id}
                        linkRecord={linkRecord}
                        showCheckbox={true}
                        displayTime={false}
                        isSelected={selectedResources.includes(linkRecord.id)}
                        onSelect={(_, selected) => {
                          console.log("selected", selected);
                          if (selected) {
                            setSelectedResources((prev) => [
                              ...prev,
                              linkRecord.id,
                            ]);
                          } else {
                            setSelectedResources((prev) =>
                              prev.filter((r) => r !== linkRecord.id)
                            );
                          }
                        }}
                        className="policy-drawer-resource-item"
                        minimum={false}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}
            {conflictsResults && (
              <div className="policy-drawer-resource-container">
                {isDirty && (
                  <div className="policy-drawer-controls-row red-text">
                    The policy content has changed since you last checked the
                    conflicts. Please regenerate the conflict.
                  </div>
                )}

                <div className="policy-drawer-result-resource-list">
                  <div className="policy-drawer-subtitle">
                    Resources
                    {conflictsResults && (
                      <div className="policy-drawer-check-conflicts">
                        <PolicyStudioActionButton
                          label="Reset"
                          onClick={() => setConflictsResults(undefined)}
                          disabled={isLoading}
                          variant="tertiary"
                        />
                      </div>
                    )}
                  </div>
                  <div className="policy-drawer-scrollable-resources">
                    {conflictsResults.linkRecordIds.map((lrId) => {
                      const linkRecord = linkRecords.find(
                        (lr) => lr.id === lrId
                      );
                      if (!linkRecord) return null;
                      return (
                        <LinkRecordActivity
                          key={linkRecord.id}
                          linkRecord={linkRecord}
                          minimum={false}
                          displayTime={false}
                        />
                      );
                    })}
                  </div>
                </div>
                <div className="policy-drawer-controls-row">
                  <div className="policy-drawer-controls-spacer"></div>
                  <div className="policy-drawer-icon-group">
                    <MyTooltip title="Copy">
                      <PolicyStudioIconButton
                        key="1"
                        // iconSrc="./icons/policy-studio-copy.svg"
                        iconComponent={<Copy size={20} />}
                        border={false}
                        alt="copy"
                        onClick={() => handleCopy()}
                        disabled={!conflictsResults}
                      />
                    </MyTooltip>
                    <MyTooltip title="Export">
                      <PolicyStudioIconButton
                        key="2"
                        // iconSrc="./icons/policy-studio-export.svg"
                        iconComponent={<Download size={20} />}
                        border={false}
                        alt="export"
                        onClick={() => {
                          if (conflictsResults?.conflictsContent) {
                            exportToPdf(
                              "conflicts-doc",
                              conflictsResults.conflictsContent
                            );
                          }
                        }}
                        disabled={!conflictsResults}
                      />
                    </MyTooltip>
                  </div>
                </div>
                <div className="policy-drawer-results-content">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: showdownConverter.makeHtml(
                        conflictsResults.conflictsContent
                      ),
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default DrawerPolicyConflicts;
